import { ArrayUtils, StringUtils, Logger, engineComponent, Utils } from "neo-common";
import { ComponentTypes } from "../../ComponentTypes";
import { StageController } from "../../StageController";
import { BaseElement } from "./BaseElement";
import { Tweens } from "../../Effects/Tweens";
import { StyleManager } from "../../Managers/StyleManager";
import { ParticleStyle, IPathOptions } from "../Styles/ParticleStyle";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../../Utils/FrameTimer";
import { AnimationElement } from "./AnimationElement";
import { ParticleElementAttributes } from "./ParticleElement";
import { CustomUtils } from "../../../Utils/CustomUtils";

/**
 * @deprecated Use ParticleElement instead ParticleAnimationElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ParticleAnimationElement extends BaseElement {
    // handle definition for particles
    private _style: ParticleStyle;
    private _styleName: string;
    //  holds particles in pool and reused them
    private _pool: Array<AnimationElement>;
    // timer for add particles
    private _timer: FrameTimerEventEmitter;

    private _stopping: boolean;
    private _stop: boolean;
    private _emitOnce: boolean;
    private _startType: string;

    private _blendMode: number;
    private _created: boolean;

    constructor() {
        super();

        this._pool = [];

        this._timer = CreateFrameTimer("particleTimer");
        this._timer.on(FrameTimerEvents.Complete, this.addParticles.bind(this));

        this._created = false;
        this._stopping = false;
        this._stop = true;
        this._emitOnce = false;
        this._startType = "";
        this.visible = false;
    }

    createByNode(node: Element): void {
        if (!this._created) {
            super.createByNode(node);

            if (node.hasAttribute(ParticleElementAttributes.BLEND_MODE)) {
                this._blendMode = parseInt(node.getAttribute(ParticleElementAttributes.BLEND_MODE));
            }

            if (
                node.hasAttribute(ParticleElementAttributes.STYLE) ||
                node.hasAttribute(ParticleElementAttributes.STYLE2)
            ) {
                if (node.hasAttribute(ParticleElementAttributes.STYLE)) {
                    this._style = <ParticleStyle>(
                        StyleManager.Styles[node.getAttribute(ParticleElementAttributes.STYLE)]
                    );
                } else {
                    this._style = <ParticleStyle>(
                        StyleManager.Styles[node.getAttribute(ParticleElementAttributes.STYLE2)]
                    );
                }

                this._styleName = this._style.name;

                this.createParticles();
                this._created = true;
            }

            if (node.hasAttribute("Speed")) {
                this.setSpeed(parseFloat(node.getAttribute("Speed")));
            }
        }
    }

    private setSpeed(speed: number): void {
        for (let i: number = 0; i < this._pool.length; i++) {
            this._pool[i].changeAnimationSpeed(speed);
        }
    }

    updateByNode(node: Element): void {
        if (node.hasAttribute(ParticleElementAttributes.EMIT_ONCE)) {
            this._emitOnce = StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.EMIT_ONCE));
        }

        if (node.hasAttribute(ParticleElementAttributes.START_TYPE)) {
            this._startType = node.getAttribute(ParticleElementAttributes.START_TYPE);
        }

        if (
            node.hasAttribute(ParticleElementAttributes.START) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.START))
        ) {
            this.start(this._emitOnce, this._startType);
        }

        if (
            node.hasAttribute(ParticleElementAttributes.STOPPING) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.STOPPING))
        ) {
            this.stopping();
        }

        if (
            node.hasAttribute(ParticleElementAttributes.STOP) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.STOP))
        ) {
            this.stop();
        }

        if (
            node.hasAttribute(ParticleElementAttributes.RESET) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.RESET))
        ) {
            this.reset();
        }

        if (
            node.hasAttribute(ParticleElementAttributes.CLEAR) &&
            StringUtils.parseBoolean(node.getAttribute(ParticleElementAttributes.CLEAR))
        ) {
            this.clear();
        }
    }

    start(emitOnce: boolean = false, startType: string): void {
        this.visible = true;
        this._emitOnce = emitOnce;
        this._startType = startType;
        this._stopping = emitOnce;
        this._stop = false;
        if (!this._emitOnce) {
            this._timer.start(this._style.time);
            if (this._startType === "now") {
                this.addParticles();
            }
        } else {
            this.addParticles();
        }
    }

    stopping(): void {
        this._stopping = true;
    }

    stop(): void {
        this._stopping = false;
        this._stop = true;
        this._timer.stop();
        this.visible = false;
    }

    reset(): void {
        for (let i: number = 0; i < this._pool.length; i++) {
            this.setupPosition(this._pool[i]);
            this._pool[i].visible = false;
            this._pool[i].stop();
        }
    }

    clear(): void {
        this._stopping = false;
        this._stop = true;
        this._timer.stop();
        this.visible = false;
        this.removeAllFromPool();
    }

    private createParticles(): void {
        if (this._style != null) {
            if (this._style.images != null && this._style.images.length > 0) {
                let elementsCount: number = this._style.calculateElements();
                let elementsResources: Array<string> = this.prepareResources(elementsCount);

                for (let i: number = 0; i < elementsCount; i++) {
                    var node: Element = document.createElement("particle");
                    node.setAttribute("Style2", elementsResources[i]);
                    node.setAttribute("RandomStartFrame", "true");
                    let imageElement: AnimationElement = new AnimationElement();
                    imageElement.create(node);
                    let particleLife: number =
                        Math.floor(Math.random() * (this._style.maxLife - this._style.minLife)) + this._style.minLife;

                    this.setupParticle(imageElement);
                    this.setupPosition(imageElement);

                    this.addPathBehaviour(imageElement, particleLife);
                    this.addScaleBehaviour(imageElement, particleLife);
                    this.addRotationBehaviour(imageElement, particleLife);
                    this.addAlphaBehaviour(imageElement, particleLife);

                    this.addChild(imageElement);
                    this._pool.push(imageElement);
                }
            }
        }
    }

    private prepareResources(count: number): Array<string> {
        let resources: Array<string> = [];
        for (let i: number = 0; i < count; i++) {
            resources.push(this._style.images[Math.floor(Math.random() * this._style.images.length)]);
        }
        return resources;
    }

    private setupParticle(imageElement: AnimationElement): void {
        imageElement.visible = false;
        imageElement.usedInPool = false;

        if (this._blendMode != null) {
            imageElement.blendMode = CustomUtils.getBlendMode(this._blendMode);
        }
    }

    private setupPosition(imageElement: AnimationElement): void {
        imageElement.pivot.x = imageElement.width / 2;
        imageElement.pivot.y = imageElement.height / 2;

        switch (this._style.positionZone.type) {
            case ParticleStyle.ZoneRectangle:
                imageElement.x = parseInt(
                    (this._style.positionZone.x + Math.random() * this._style.positionZone.width).toFixed(0),
                );
                imageElement.y = parseInt(
                    (this._style.positionZone.y + Math.random() * this._style.positionZone.height).toFixed(0),
                );
                break;
            case ParticleStyle.ZoneLine:
                // TODO
                break;
            /*case ParticleStyle.ZoneCircle:
                // TODO
                break;*/
            case ParticleStyle.ZoneEllipse:
                // TODO
                break;
        }
    }

    private addPathBehaviour(imageElement: AnimationElement, life: number): void {
        if (this._style.pathValues != null) {
            let points: Array<Object> = [];
            for (let i: number = 0; i < this._style.pathValues.length; i++) {
                if (
                    this._style.pathValues[i] != null &&
                    this._style.pathValues[i].x != null &&
                    this._style.pathValues[i].y != null
                ) {
                    let valueX: number = this._style.pathValues[i].x;
                    let valueY: number = this._style.pathValues[i].y;
                    if (this._style.pathValues[i].randomX != null) {
                        valueX =
                            Math.floor(
                                Math.random() * (this._style.pathValues[i].randomX - this._style.pathValues[i].x),
                            ) + this._style.pathValues[i].x;
                    }
                    if (this._style.pathValues[i].randomY != null) {
                        valueY =
                            Math.floor(
                                Math.random() * (this._style.pathValues[i].randomY - this._style.pathValues[i].y),
                            ) + this._style.pathValues[i].y;
                    }
                    points.push({ x: imageElement.x + valueX, y: imageElement.y + valueY });
                }
            }
            if (points.length > 2) {
                Tweens.positionTween(imageElement, {
                    bezier: {
                        values: points,
                    },
                    tweenX: [imageElement.x, imageElement.x],
                    tweenY: [imageElement.y, imageElement.y],
                    tweenDuration: life,
                    paused: false,
                    callbackFunction: this.removeFromPool,
                    callbackParams: [imageElement],
                    callbackScope: null,
                });
            } else if (points.length === 2) {
                Tweens.positionTween(imageElement, {
                    tweenX: [points[0]["x"], points[1]["x"]],
                    tweenY: [points[0]["y"], points[1]["y"]],
                    tweenDuration: life,
                    paused: false,
                    callbackFunction: this.removeFromPool,
                    callbackParams: [imageElement],
                    callbackScope: null,
                });
            }
        }
    }

    private addScaleBehaviour(imageElement: AnimationElement, life: number): void {
        if (this._style.scaleValues != null) {
            let scalePoints: Array<PIXI.Point> = [];
            for (let i: number = 0; i < this._style.scaleValues.length; i++) {
                let scaleValue: number;
                if (this._style.scaleValues[i].length > 1) {
                    scaleValue = parseFloat(
                        (
                            Math.random() * (this._style.scaleValues[i][1] - this._style.scaleValues[i][0]) +
                            this._style.scaleValues[i][0]
                        ).toFixed(1),
                    );
                } else {
                    scaleValue = this._style.scaleValues[i][0];
                }
                scalePoints.push(new PIXI.Point(scaleValue, scaleValue));
            }

            if (scalePoints.length === 1) {
                imageElement.scale.x = scalePoints[0].x;
                imageElement.scale.y = scalePoints[0].y;
            } else {
                Tweens.scaleTween(imageElement, {
                    tweenValues: scalePoints,
                    tweenDuration: life,
                    paused: false,
                });
            }
        }
    }

    private addRotationBehaviour(imageElement: AnimationElement, life: number): void {
        if (this._style.rotationValues != null && this._style.rotationValues.length > 1) {
            let rotationAngles: Array<number> = [];
            for (let i: number = 0; i < this._style.rotationValues.length; i++) {
                let scaleValue: number;
                if (this._style.rotationValues[i].length > 1) {
                    scaleValue =
                        Math.random() * (this._style.rotationValues[i][1] - this._style.rotationValues[i][0]) +
                        this._style.rotationValues[i][0];
                } else {
                    scaleValue = this._style.rotationValues[i][0];
                }

                rotationAngles.push(scaleValue);
            }

            Tweens.rotationTween(imageElement, {
                degrees: [rotationAngles[0], rotationAngles[1]],
                tweenDuration: life,
                paused: false,
            });
        }
    }

    private addAlphaBehaviour(imageElement: AnimationElement, life: number): void {
        if (this._style.alphaValues != null) {
            let alphas: Array<number> = [];

            for (let i: number = 0; i < this._style.alphaValues.length; i++) {
                let alpha: number;
                if (this._style.alphaValues[i].length > 1) {
                    alpha =
                        Math.random() * (this._style.alphaValues[i][1] - this._style.alphaValues[i][0]) +
                        this._style.alphaValues[i][0];
                } else {
                    alpha = this._style.alphaValues[i][0];
                }

                alphas.push(alpha);
            }

            Tweens.alphaTween(imageElement, {
                alphas: alphas,
                tweenDuration: life,
                paused: false,
            });
        }
    }

    private addParticles(): void {
        if (!this._stop) {
            this.getFromPool();

            if (!this._stopping && this._style.time > 0) {
                this._timer.start(this._style.time);
            }
        }
    }

    private getFromPool(): void {
        this._pool.sort(function () {
            return 0.5 - Math.random();
        });

        let counter: number = 0;
        for (let i: number = 0; i < this._pool.length; i++) {
            if (counter === this._style.count) {
                return;
            }
            if (!this._pool[i].usedInPool) {
                this._pool[i].usedInPool = true;
                this._pool[i].visible = true;
                this._pool[i].playMe();
                counter++;
                Tweens.restartTweens(this._pool[i].uid);
            }
        }
    }

    private removeFromPool(imageElement: AnimationElement): void {
        imageElement.visible = false;
        imageElement.usedInPool = false;
        imageElement.stop();
    }

    private removeAllFromPool(): void {
        for (let i: number = 0; i < this._pool.length; i++) {
            this._pool[i].visible = false;
            this._pool[i].usedInPool = false;
            // TODO stop all tweens
        }
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ParticleAnimationElement
     */
    clone(): ParticleAnimationElement {
        var element: ParticleAnimationElement = new ParticleAnimationElement();
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;
        element._blendMode = this._blendMode;
        var node: Element = document.createElement("particle");
        node.setAttribute("Style2", this._styleName);
        element.create(node);
        element._markAsCloned(); // must be called last before return!
        return element;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ParticleAnimationElement.ts
 **/